import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Chip,
  Box,
  Divider,
  alpha,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import EventIcon from "@mui/icons-material/Event";
import CategoryIcon from "@mui/icons-material/Category";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import dayjs from "dayjs";

const SEVERITY_CONFIG = {
  critical: { label: "Critical", bg: "#FEE2E2", color: "#991B1B" },
  high: { label: "High", bg: "#FFEDD5", color: "#9A3412" },
  medium: { label: "Medium", bg: "#FEF9C3", color: "#854D0E" },
  low: { label: "Low", bg: "#DBEAFE", color: "#1E40AF" },
};

const STATUS_CONFIG = {
  open: { label: "Open", bg: "#FEE2E2", color: "#991B1B" },
  in_progress: { label: "In Progress", bg: "#DBEAFE", color: "#1E40AF" },
  resolved: { label: "Resolved", bg: "#D1FAE5", color: "#065F46" },
};

function DetailSection({ label, value }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function BugReportDetailDialog({ report, open, onClose }) {
  if (!report) return null;

  const severityCfg = SEVERITY_CONFIG[report.severity] || SEVERITY_CONFIG.medium;
  const statusCfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.open;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      {/* Gradient Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #EF4444 0%, #F97316 100%)",
          px: 3,
          py: 2.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, mr: 2 }}>
            <BugReportIcon sx={{ color: "white" }} />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: "white" }}
            >
              {report.title}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75}>
            <Chip
              label={statusCfg.label}
              size="small"
              sx={{
                height: 26,
                fontWeight: 700,
                bgcolor: alpha("#fff", 0.9),
                color: statusCfg.color,
              }}
            />
            <Chip
              label={severityCfg.label}
              size="small"
              sx={{
                height: 26,
                fontWeight: 700,
                bgcolor: alpha("#fff", 0.9),
                color: severityCfg.color,
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          {/* Meta info */}
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {dayjs(report.createdAt).format("MMM D, YYYY h:mm A")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CategoryIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {report.module}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PriorityHighIcon fontSize="small" color="action" />
              <Chip
                label={severityCfg.label}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: severityCfg.bg,
                  color: severityCfg.color,
                }}
              />
            </Stack>
          </Stack>

          <Divider />

          {/* Content sections */}
          <DetailSection label="Description" value={report.description} />
          <DetailSection label="Steps to Reproduce" value={report.stepsToReproduce} />
          <DetailSection label="Expected Behavior" value={report.expectedBehavior} />
          <DetailSection label="Actual Behavior" value={report.actualBehavior} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
